import React, { useState, useMemo } from 'react';
import { X, Save, Upload, FileText, MessageSquare, ExternalLink, Calendar, User, Eye } from 'lucide-react';
import { KTTask, Document, SelectedProject, SharePointLink, EmailDoc } from '../types/kt-type.ts';
import { getStatusColor, getStatusIcon, mapReviewStatusToDocumentStatus } from '../utils/statusHelpers.ts';

interface TaskModalProps {
  isOpen: boolean;
  task: KTTask | null;
  selectedProject: SelectedProject | null;
  onClose: () => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ isOpen, task, selectedProject, onClose }) => {
  const [newDocument, setNewDocument] = useState<File | null>(null);

  // Parse SharePoint links and Email docs from JSON strings
  const sharePointLinks = useMemo<SharePointLink[]>(() => {
    if (!selectedProject?.SharePointLinks) return [];
    try {
      return JSON.parse(selectedProject.SharePointLinks);
    } catch {
      return [];
    }
  }, [selectedProject?.SharePointLinks]);

  const emailDocs = useMemo<EmailDoc[]>(() => {
    if (!selectedProject?.EmailDocs) return [];
    try {
      return JSON.parse(selectedProject.EmailDocs);
    } catch {
      return [];
    }
  }, [selectedProject?.EmailDocs]);

  // Convert project documents to our Document format
  const documents = useMemo<Document[]>(() => {
    if (!selectedProject?.documents) return [];
    return selectedProject.documents.map((doc, index) => ({
      id: `doc-${index}`,
      name: doc.name,
      status: mapReviewStatusToDocumentStatus(doc.review_status),
      type: 'mandatory' as const,
      feedback: doc.review_comments || undefined,
      path: doc.path,
      upload_time: doc.upload_time,
      uploaded_by: doc.uploaded_by
    }));
  }, [selectedProject?.documents]);

  const [additionalDocuments, setAdditionalDocuments] = useState<Document[]>([]);

  if (!isOpen || !task || !selectedProject) return null;

  const handleDocumentUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setNewDocument(file);
    }
  };

  const handleAddDocument = () => {
    if (newDocument) {
      const newDoc: Document = {
        id: `additional-${Date.now()}`,
        name: newDocument.name,
        status: 'pending',
        type: 'employee-provided'
      };
      setAdditionalDocuments([...additionalDocuments, newDoc]);
      setNewDocument(null);
    }
  };

  const handleViewDocument = (doc: Document) => {
    if (doc.path) {
      // For production, you would typically construct the full URL to your file server
      // For now, we'll create a placeholder URL structure
      const fileUrl = `${process.env.REACT_APP_WEB_TOOL_API_BASE_URL}/documents/${doc.name}`;
      
      // Check if it's a PDF or image that can be viewed in browser
      const viewableExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif', '.txt', '.html'];
      const fileExtension = doc.name.toLowerCase().substring(doc.name.lastIndexOf('.'));
      
      if (viewableExtensions.includes(fileExtension)) {
        // Open in new tab for viewing
        window.open(fileUrl, '_blank', 'noopener,noreferrer');
      } else {
        // For other file types, trigger download
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = doc.name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } else {
      // Fallback for documents without path
      alert('Document path not available');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  console.log("Selected Project in TaskModal", selectedProject);

  return (
    <div style={{zIndex:"99999"}} className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">KT Tasks - {selectedProject.RoleForKT}</h2>
            <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
              <div className="flex items-center gap-1">
                <User className="w-4 h-4" />
                <span>KT Manager: {selectedProject.manager_email}</span>
              </div>
              <div className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                <span>Session: {selectedProject.KtSessionTime}</span>
              </div>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                selectedProject.KT_Status === 'In Progress' 
                  ? 'bg-blue-100 text-blue-800' 
                  : selectedProject.KT_Status === 'Completed'
                  ? 'bg-green-100 text-green-800'
                  : 'bg-gray-100 text-gray-800'
              }`}>
                {selectedProject.KT_Status}
              </span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {selectedProject.ProjectName !== null ? (
          <>
          <div className="p-6 space-y-8">
            {/* Project Overview */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{selectedProject.ProjectName}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{selectedProject.ProjectDescription}</p>
              <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
                <span>Department: {selectedProject.Department}</span>
                <span>•</span>
                <span>KT Department: {selectedProject.KT_Department}</span>
                <span>•</span>
                <span>End Date: {formatDate(selectedProject.EndDate)}</span>
              </div>
            </div>

            {/* Mandatory Documents */}
            {documents.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Project Documents</h3>
                <div className="space-y-3">
                  {documents.map((doc) => {
                    const StatusIcon = getStatusIcon(doc.status);
                    return (
                      <div key={doc.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3 flex-1">
                            <FileText className="w-5 h-5 text-gray-400" />
                            <div className="flex-1">
                              <span className="text-sm font-medium text-gray-900">{doc.name}</span>
                              {doc.upload_time && (
                                <p className="text-xs text-gray-500">
                                  Uploaded: {formatDate(doc.upload_time)} by {doc.uploaded_by}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleViewDocument(doc)}
                              className="inline-flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-700 transition-colors"
                            >
                              <Eye className="w-3 h-3" />
                              View
                            </button>
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(doc.status)}`}>
                              <StatusIcon className="w-4 h-4" />
                              {doc.status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </div>
                        </div>
                        {doc.feedback && (
                          <div className={`mt-3 p-3  border border-red-200 rounded-lg ${(doc.status).toLowerCase() === 'approved' ? ' bg-green-50' : ' bg-red-50'}`}>
                            <div className="flex items-start gap-2">
                              <MessageSquare className={`w-4 h-4  mt-0.5 ${(doc.status).toLowerCase() === 'approved' ? ' text-green-600' : ' text-red-600'}`} />
                              <div>
                                <p className={`text-sm font-medium  ${(doc.status).toLowerCase() === 'approved' ? ' text-green-800' : ' text-red-800'}`}>Manager Feedback {doc.status}</p>
                                <p className={`text-sm font-medium  ${(doc.status).toLowerCase() === 'approved' ? ' text-green-700' : ' text-red-700'}`}>{doc.feedback}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* SharePoint Links */}
            {sharePointLinks.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Important SharePoint Resources</h3>
                <div className="space-y-3">
                  {sharePointLinks.map((link, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{link.name}</span>
                        <a
                          href={link.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                        >
                          Open Link
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </div>
                      <p className="text-sm text-gray-600">{link.description}</p>
                      <p className="text-xs text-gray-500 mt-1">URL: {link.url}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Email Documents */}
            {emailDocs.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Important Email Communications</h3>
                <div className="space-y-3">
                  {emailDocs.map((email, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-gray-900">{email.subject}</span>
                        <span className="text-xs text-gray-500">{formatDate(email.date)}</span>
                      </div>
                      <p className="text-sm text-gray-600">{email.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

          

            {/* KT Project Description */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Knowledge Transfer Project Description</h3>
              <div className="space-y-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Key Responsibilities</label>
                  <div className="p-4 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 leading-relaxed shadow-sm">
                    {selectedProject.KeyResponsibilities}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Critical Projects & Information</label>
                  <div className="p-4 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 leading-relaxed shadow-sm">
                    {selectedProject.CriticalProjectsInfo}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Description</label>
                  <div className="p-4 bg-white border border-gray-300 rounded-lg text-sm text-gray-900 leading-relaxed shadow-sm">
                    {selectedProject.ProjectDescription}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Current Role</label>
                    <div className="text-sm font-medium text-gray-900">{selectedProject.Role}</div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">KT Role</label>
                    <div className="text-sm font-medium text-gray-900">{selectedProject.RoleForKT}</div>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Knowledge Manager Feedback FocusArea</h3>
              <div className="space-y-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-6 border border-gray-200">
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Project Description</label>
                  <div className="p-4 bg-red-50 border border-gray-300 rounded-lg text-sm text-gray-900 leading-relaxed shadow-sm">
                    {selectedProject?.KTFocusAreas?.ProjectDescription}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Key Responsibilities</label>
                  <div className="p-4 bg-red-50 border border-gray-300 rounded-lg text-sm text-gray-900 leading-relaxed shadow-sm">
                    {selectedProject?.KTFocusAreas?.KeyResponsibilities }
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Critical Projects & Information</label>
                  <div className="p-4 bg-red-50 border border-gray-300 rounded-lg text-sm text-gray-900 leading-relaxed shadow-sm">
                    {selectedProject?.KTFocusAreas?.CriticalProjectsInfo}
                  </div>
                </div>
              
            
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
              <button
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            
            </div>
          </div>
        </>
        ): (
          <div className="p-6 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">This KT Task is {selectedProject.KT_Status}</h3>
            <p className="text-gray-600 mb-4">You can view the documents and resources shared after the Add Task.</p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default TaskModal;